//
// KD在庫管理システム[375]  発注依頼画面
//
// InventoryOrder.js
//

import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from "react-router-dom";
import '../styles/InventoryOrder.css';
import OrderRequestModal from "./orderRequestModal";
import DoubleConfirmModal from "./doubleConfirmModal";

const InventoryOrder = ({ user, onNavigateToCheckout }) => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [tabMode, setTabMode] = useState('shelf');
  const [activeCategory, setActiveCategory] = useState('すべて');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showOrderRequestModal, setShowOrderRequestModal] = useState(false);
  const [clock, setClock] = useState('');
  const [modalConfig, setModalConfig] = useState(null);
  const [conflictOrders, setConflictOrders] = useState([]); // 重複発注チェック結果
  const [orderRequestData, setOrderRequestData] = useState([]); // 最新の発注依頼データ
  
  const orderPanelRef = useRef(null);

 // 🔸サーバー上の orderRequestData.json を常に最新で読み込む
 useEffect(() => {
   fetch("https://newells4610.stars.ne.jp/data/orderRequestData.json", {
     cache: "no-store",
   })
     .then((res) => res.json())
     .then((data) => setOrderRequestData(data))
     .catch((err) => {
       console.error("orderRequestData.json の読み込みに失敗:", err);
       setOrderRequestData([]);
     });
 }, []);

  // アイテムリスト追加時スクロール ------------------------------------------------------
  useEffect(() => {
    setTimeout(() => {
      if (orderPanelRef.current) {
        orderPanelRef.current.scrollTop = orderPanelRef.current.scrollHeight;
      }
    }, 100);
  }, [selectedItems]);

  // 時計用 useEffect --------------------------------------------------------------------
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
      const formatted = `${now.getFullYear()}/${(now.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${now
        .getDate()
        .toString()
        .padStart(2, '0')}（${dayNames[now.getDay()]}） ${now
        .getHours()
        .toString()
        .padStart(2, '0')}:${now
        .getMinutes()
        .toString()
        .padStart(2, '0')}:${now
        .getSeconds()
        .toString()
        .padStart(2, '0')}`;
      setClock(formatted);
    };

    updateClock();
    const timer = setInterval(updateClock, 1000);
    return () => clearInterval(timer);
  }, []);

  // 在庫データ読み込み ------------------------------------------------------------------
  useEffect(() => {
    fetch("https://newells4610.stars.ne.jp/data/KDinventoryData.json?" + Date.now())
      .then(res => res.json())
      .then(setItems)
      .catch(err => {
        console.error("在庫データ取得失敗:", err);
        setModalConfig({
          message: "在庫データを取得できませんでした。\nネット接続をご確認ください。",
          onConfirm: () => setModalConfig(null),
        });
      });
  }, []);

  // 在庫データ再読み込み --------------------------------------------------------------------

  const fetchInventoryFromServer = async () => {
    try {
      const res = await fetch("https://newells4610.stars.ne.jp/data/KDinventoryData.json?ts=${Date.now()}", {
        cache: "no-store"
      });
      const data = await res.json();
      setItems(data);
      
      setModalConfig({
        message: "✨✨在庫データを最新に更新しました！✨✨",
        onConfirm: () => setModalConfig(null),
      });

    } catch (err) {
      console.error("在庫データの再取得に失敗しました", err);
      
      setModalConfig({
        message: "在庫データの更新に失敗しました…😢",
        onConfirm: () => setModalConfig(null),
      });
    }
  };

// --------------------------------------------------------------------------------------

  const shelves = [...new Set(items.map(item => item.shelf).filter(Boolean))].sort();
  const categories = [...new Set(items.map(item => item.category).filter(Boolean))].sort();

  const filteredItems =
    (tabMode === 'reorder'
      ? items.filter(item => parseFloat(item.stok) <= parseFloat(item.reorderThreshold))
      : activeCategory === 'すべて'
        ? items
        : tabMode === 'category'
          ? items.filter(item => item.category === activeCategory)
          : items.filter(item => item.shelf === activeCategory)
    ).sort((a, b) => (a.productId || '').localeCompare(b.productId || ''));

  const handleAddItem = (item) => {
    const existing = selectedItems.find(i => i.id === item.id);
    
    if (existing) {
      setSelectedItems(selectedItems.map(i =>
        i.id === item.id ? { ...i, orderQuantity: i.orderQuantity + 1 } : i
      ));
    } else {
      setSelectedItems([...selectedItems, { ...item, orderQuantity: 1 }]);
    }
  };

  const handleAddNew = () => {
    const maxId = items.reduce((max, item) => Math.max(max, Number(item.id || 0)), 0);
    const newItem = {
      id: maxId + 1,
      name: "",
      productId: "",
      stok: 0,
      unit: "個",
      shelf: "",
      shelfLevel: "",
      category: "",
      memo: "",
      oldShelfNumber: "",
      image: "",
      quantityPerBox: "",
      reorderThreshold: 5,
      maxCapacity: 1000,
      wholesaler: ""
    };
    setEditingItem(newItem);
  };

  const changeQuantity = (item, delta) => {
    const updated = selectedItems.map(i => {
      if (i.id === item.id) {
        return { ...i, orderQuantity: Math.max(1, i.orderQuantity + delta) };
      }
      return i;
    });
    setSelectedItems(updated);
  };

  const updateQuantityDirect = (item, newQuantity) => {
    const parsed = parseFloat(newQuantity);
    if (isNaN(parsed)) return;

    setSelectedItems(prev =>
      prev.map(i =>
        i.id === item.id
          ? { ...i, orderQuantity: Math.max(0.01, parsed) }
          : i
      )
    );
  };

  const removeItem = (item) => {
    setSelectedItems(prev => prev.filter(i => i.id !== item.id));
  };

  const handleConfirmOrder = () => {
    if (!user || !user.userId?.trim()) {
      setModalConfig({
        message: "ログインしてください。😡",
        onConfirm: () => setModalConfig(null),
      });
      return;
    }

    if (selectedItems.length === 0) {
      setModalConfig({
        message: "発注依頼リストが空です。🙄",
        onConfirm: () => setModalConfig(null),
      });
      return;
    }
    
    // 🔸 orderRequestData.json を都度最新で取得（他端末の更新も反映）
    fetch("https://newells4610.stars.ne.jp/data/orderRequestData.json", {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((orderRequestData) => {
        // 🔸 重複発注チェック
        const conflicts = selectedItems.flatMap((sel) =>
          orderRequestData.filter(
            (req) =>
              req.oldShelfNumber === sel.oldShelfNumber &&
              Number(req.inStok) === 0
          )
        );

        setConflictOrders(conflicts);
        setShowConfirmModal(true);
      })
      .catch((err) => {
        console.error("発注データの取得に失敗:", err);
        setModalConfig({
          message: "過去の発注データを取得できませんでした。",
          onConfirm: () => setModalConfig(null),
        });
      });
  };

  const proceedWithOrder = async () => {
    const orderRequests = selectedItems.map(item => ({
      inStok: 0, // 未入荷
      scheduleDay: "",
      orderDay: "",
      timestamp: new Date().toLocaleString("sv-SE"), // 日本時間で保存
      userId: user.userId,
      oldShelfNumber: item.oldShelfNumber || "",
      name: item.name,
      productId: item.productId,
      orderQuantity: item.orderQuantity,
      unit: item.unit || "個",
      wholesaler: item.wholesaler || ""
    }));

    try {
      const res = await fetch("https://newells4610.stars.ne.jp/api/add_order_request.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orders: orderRequests })
      });
      
      const json = await res.json();

      if (!res.ok || json.status !== "ok") {
        throw new Error(json.message || "発注依頼登録失敗");
      }

      setSelectedItems([]);
      setShowConfirmModal(false);
      
      setModalConfig({
        message: "発注依頼を登録しました！✨",
        onConfirm: () => setModalConfig(null),
      });

    } catch (err) {
      setModalConfig({
        message: `発注依頼登録エラー: ${err.message}`,
        onConfirm: () => setModalConfig(null),
      });
    }
  };

  // ---- 画面 -------------------------------------------------------------

  return (
    <>
      {/* 🚨 縦向き警告メッセージ */}
      <div className="rotate-warning" style={{
        position: 'fixed',
        top: 0, left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#fff',
        zIndex: 9999,
        display: 'none',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '1.5rem',
        textAlign: 'center',
        padding: '20px'
      }}>
        📱このアプリは横向き専用です<br />
        端末を横にしてご利用ください
      </div>

  	{/* 発注依頼メイン ----------------------------------------------------- */}

      <div className="inventory-order">
        <div className="header-order">
          <h2 className="title-heading-order"
            onClick={fetchInventoryFromServer}
            style={{ cursor: 'pointer' }}
          >
            KD在庫管理[375]
            {user?.isAdmin && (
              <>
                <span className="admin-mode-label">管理mode</span>
                <span className="order-mode-label">発注依頼</span>
              </>
            )}
          </h2>
          
          <div className="user-display-order">
            お疲れ様です！ {user?.company ? `${user.company}・` : ''}{user?.name || user?.userId} さん
          </div>
          <div className="clock-display-order">{clock}</div>
        </div>

        <div className="tab-switcher-order">
          <button
            onClick={() => {
              setTabMode('shelf');
              setActiveCategory('すべて');
            }}
            className={tabMode === 'shelf' ? 'active' : ''}
          >
            棚番 別
          </button>

          <button
            onClick={() => {
              setTabMode('category');
              setActiveCategory('すべて');
            }}
            className={tabMode === 'category' ? 'active' : ''}
          >
            カテゴリ別
          </button>

          <button
            onClick={() => setTabMode('reorder')}
            className={tabMode === 'reorder' ? 'active reorder' : 'reorder'}
          >
            ⚠ 発注対象
          </button>

          <button
            onClick={onNavigateToCheckout}
            className="checkout-nav-button"
          >
            部材編集画面へ
          </button>

          <button
            onClick={() => {
              setModalConfig({
                message: "ログアウトしますか？",
                showCancel: true,
                onConfirm: () => {
                  setModalConfig(null);

                  // サーバー側セッション削除
                  fetch("https://newells4610.stars.ne.jp/api/logout.php", { method: "POST" })
                    .then(res => res.json())
                    .then(data => {
                      console.log("サーバーログアウト:", data);
                    })
                    .catch(err => {
                      console.error("サーバーログアウトエラー:", err);
                    })
                    .finally(() => {

                      // フロント側もクリア
                      localStorage.removeItem("user");
                      window.location.reload();
                    });
                },
                onCancel: () => setModalConfig(null),
              });
            }}
            className="logout-button"
          >
            ログアウト
          </button>

          <button
            onClick={() => setShowOrderRequestModal(true)}
            className="order-list-button"
          >
            発注依頼一覧
          </button>
        </div>

        <div className="category-tabs-order">
          {tabMode === 'reorder' ? (
            <div className="reorder-message" style={{ padding: '3px', color: 'red' }}>
              ⚠  在庫が少なくなっている商品です。発注をご検討ください。
            </div>
          ) : (
            <>
              {user?.isAdmin && (
                <div className="admin-controls-order">
                  <button onClick={handleAddNew}>＋ 新規追加</button>
                </div>
              )}

              {(tabMode === 'category' ? categories : shelves).map(value => (
                <button
                  key={value}
                  className={activeCategory === value ? 'active' : ''}
                  onClick={() => setActiveCategory(value)}
                >
                  {value}
                </button>
              ))}
            </>
          )}
        </div>

        <div className="main-content-order">
          <div className="item-list-order">
            {filteredItems.map(item => (
              <div
                key={`${item.id}-${item.stock}`}
                className="item-card-order"

              // 左クリック(PCマウス操作) or タップ(モバイル)
                onClick={() => handleAddItem(item)}
              >
                <img src={item.image || "/images/NoImage.jpg"} alt={item.name} width="80" />
                <div className="item-info-order">
                  <strong className="item-name-order">
                    {item.name}
                  </strong>
                  <p className="item-id">品番: {item.productId}</p>
                  <p className="item-stock-order"
                    style={{
                      color: parseFloat(item.stok) <= parseFloat(item.reorderThreshold || 0) ? 'red' : 'inherit',
                    }}
                  >
                    在庫：
                    <span className={parseFloat(item.stok) <= 0 ? 'out-of-stock' : ''}>
                      {Number(item.stok ?? 0).toFixed(2)} {item.unit || ''}
                    </span>
                  </p>
                  <p className="item-quantityPerBox">入数: {item.quantityPerBox}</p>
                  <p className="item-capacity">収納数: {item.maxCapacity || '-'}</p>
                  <p className="memo">・{item.memo}</p>
                </div>
              </div>
            ))}
          </div>

{/* ---------- 右パネル ---------- */}

          <div className="order-panel">
            <h3>📋発注依頼リスト</h3>
            <div className="order-panel-scroll" ref={orderPanelRef}>
              {selectedItems.map((item) => (
                <div key={item.id} className="order-item">
                  <div className="order-name">{item.name}</div>
                  <div className="order-controls">
                    <button onClick={() => changeQuantity(item, -1)}>-</button>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={item.orderQuantity}
                      onChange={(e) => updateQuantityDirect(item, e.target.value)}
                    />
                    <span className="item-unit">{item.unit || "個"}</span>
                    <button onClick={() => changeQuantity(item, 1)}>+</button>
                    <button onClick={() => removeItem(item)}>🗑</button>
                  </div>
                </div>
              ))}
            </div>
            <button
              className="order-confirm-button"
              onClick={handleConfirmOrder}
            >
              発注依頼！
            </button>
          </div>
        </div>
      </div>

      {/* 発注確認モーダル */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            
            {/* 未入荷の同商品があれば警告表示 */}
            {conflictOrders.length > 0 && (
              <div style={{ color: "red", marginBottom: "10px", fontSize: "14px" }}>
                発注依頼済み未入荷の同商品があります！
                <ul style={{ textAlign: "left", marginTop: "6px", fontSize: "12px" }}>
                  {conflictOrders.map((c, i) => (
                    <li key={i}>
                      {c.name}（品番: {c.productId}）× {c.orderQuantity} {c.unit || "個"}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <h3>この内容で発注依頼を確定しますか？</h3>
            
            <div className="modal-list">
              {selectedItems.map((item) => (
                <div key={item.id} className="modal-item">
                  {item.name}（品番: {item.productId}）× {item.orderQuantity} {item.unit || "個"}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button onClick={proceedWithOrder}>
                ✅ 確定
              </button>
              <button onClick={() => setShowConfirmModal(false)}>❌ キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* 汎用確認モーダル */}
        {modalConfig && <DoubleConfirmModal {...modalConfig} />}


    {/* 
      {modalConfig && (
        <div className="modal-overlay">
          <div className="modal-box">
            <p style={{ whiteSpace: 'pre-wrap' }}>{modalConfig.message}</p>
            <div className="modal-actions">
              <button onClick={modalConfig.onConfirm}>OK</button>
              {modalConfig.showCancel && (
                <button onClick={modalConfig.onCancel}>キャンセル</button>
              )}
            </div>
          </div>
        </div>
      )}
    */}


      {/* 発注依頼一覧モーダル */}
      {showOrderRequestModal && (
        <OrderRequestModal
          onClose={() => setShowOrderRequestModal(false)}
          currentUser={user}
        />
      )}
    </>
  );
};

export default InventoryOrder;